import { apiClient } from './client'

export type PromptScope =
  | { kind: 'builtin' }
  | { kind: 'organization'; orgId: number }
  | { kind: 'project'; orgId: number; projectId: number }

/**
 * Create/copy destination as the client expresses it: org identity is
 * always the caller's own (the server ignores any client-supplied orgId
 * and resolves it from the authenticated actor), so the client only ever
 * names a kind and, for 'project', which project.
 */
export type CreateScope =
  | { kind: 'builtin' }
  | { kind: 'organization' }
  | { kind: 'project'; projectId: number }

export interface PromptFields {
  title: string
  description: string | null
  body: string
}

export interface OwnedPrompt extends PromptFields {
  id: string
  scope: PromptScope
  createdBy: number
  createdAt: string
  updatedAt: string
  usageCount: number
  lastUsedAt: string | null
}

export interface PromptList {
  items: OwnedPrompt[]
  nextCursor: string | null
}

export interface PromptListQuery {
  view: 'organization' | 'project' | 'all' | 'builtin'
  projectId?: number
  q?: string
  cursor?: string
  limit?: number
}

export interface PromptCapabilities {
  mode: 'legacy' | 'owned' | 'maintenance'
  canManageCatalog: boolean
  canSeeOrg: boolean
  canEditOrg: boolean
  canImportBuiltin: boolean
  canCreateProject: boolean
  canAuthorAllProjects: boolean
  managedProjectIds: number[]
}

function queryString(query: PromptListQuery): string {
  const params = new URLSearchParams({ view: query.view })
  if (query.projectId !== undefined) params.set('projectId', String(query.projectId))
  if (query.q !== undefined) params.set('q', query.q)
  if (query.cursor !== undefined) params.set('cursor', query.cursor)
  if (query.limit !== undefined) params.set('limit', String(query.limit))
  return params.toString()
}

export const ownedPromptsApi = {
  capabilities: () => apiClient.get<PromptCapabilities>('/owned-prompts/capabilities'),
  list: (query: PromptListQuery) =>
    apiClient.get<PromptList>(`/owned-prompts?${queryString(query)}`),
  get: (id: string) => apiClient.get<OwnedPrompt>(`/owned-prompts/${encodeURIComponent(id)}`),
  create: (scope: CreateScope, fields: PromptFields) =>
    apiClient.post<OwnedPrompt>('/owned-prompts', {
      scope: scope.kind,
      ...(scope.kind === 'project' ? { projectId: scope.projectId } : {}),
      ...fields,
    }),
  update: (id: string, fields: PromptFields, expectedUpdatedAt: string) =>
    apiClient.patch<OwnedPrompt>(`/owned-prompts/${encodeURIComponent(id)}`, {
      ...fields,
      expectedUpdatedAt,
    }),
  copy: (
    id: string,
    destination: CreateScope & { kind: 'organization' | 'project' },
    fields: PromptFields
  ) =>
    apiClient.post<OwnedPrompt>(`/owned-prompts/${encodeURIComponent(id)}/copy`, {
      destination: destination.kind,
      ...(destination.kind === 'project' ? { projectId: destination.projectId } : {}),
      ...fields,
    }),
  archive: (id: string) => apiClient.post<void>(`/owned-prompts/${encodeURIComponent(id)}/archive`),
}
